import { useGetAmenitiesQuery } from "../../../services/bClientApi"
import { AMENITY_TAG_OPTIONS, AmenityType } from "../../../Types/amenityTypes"
import QuickSelectModal, { QuickSelectOption } from "../QuickSelectModal/QuickSelectModal"

export default function SelectAmenityModal(props: {
    active: boolean
    onClose: () => void
    onSelect: (amenityData: AmenityType | AmenityType[]) => void
    message?: string
    title?: string
    multiSelect?: boolean
    filterFn?: (amenity: AmenityType) => boolean
}) {

    const {
        active,
        onClose,
        onSelect,
        message = "Please select an amenity",
        title = "Choose Amenity",
        multiSelect = false,
        filterFn,
    } = props

    const { data: amenitiesData, isLoading } = useGetAmenitiesQuery({
        amenitiesQuery: {},
        pagination: { page: 1, limit: 1000 },
    })

    const amenitiesList: AmenityType[] = amenitiesData?.docs || []

    const map: Record<string, AmenityType> = {}
    amenitiesList.forEach((amenity) => {
        if (amenity._id) map[amenity._id] = amenity
    })
    const amenitiesById = map

    const filteredAmenities = amenitiesList
        .filter((amenity) => {
            if (!amenity._id) return false
            if (filterFn && !filterFn(amenity)) return false
            return true
        })
        .sort((a, b) => a.title.localeCompare(b.title))

    const options: QuickSelectOption[] = filteredAmenities.map((amenity) => ({
        id: amenity._id!,
        label: amenity.title,
        secondaryLabel: amenity.type,
        data: { tags: amenity.tags },
    }))

    const availableTags = new Set<string>()
    filteredAmenities.forEach((amenity) => {
        amenity.tags?.forEach((tag) => availableTags.add(tag))
    })
    const tagSelectOptions = AMENITY_TAG_OPTIONS
        .filter((tagOption) => availableTags.has(tagOption.tag))
        .map((tagOption) => ({ value: tagOption.tag, text: tagOption.text }))

    const handleSelect = (selected: string | string[]) => {
        if (multiSelect && Array.isArray(selected)) {
            const amenityDataList = selected
                .map(id => amenitiesById[id])
                .filter(a => a && a._id)
            if (amenityDataList.length > 0) {
                onSelect(amenityDataList)
            }
        } else if (typeof selected === 'string') {
            const amenityData = amenitiesById[selected]
            if (amenityData && amenityData._id) {
                onSelect(amenityData)
            }
        }
        onClose()
    }

    return (
        <QuickSelectModal
            active={active}
            onClose={onClose}
            onSelect={handleSelect}
            options={options}
            title={title}
            message={message}
            multiSelect={multiSelect}
            isLoading={isLoading}
            filterPlaceholder="Filter Amenities"
            filterFields={tagSelectOptions.length > 0 ? [
                {
                    fieldName: 'tag',
                    fieldLabel: 'Tag',
                    fieldType: 'select',
                    placeholder: 'All Tags',
                    options: tagSelectOptions,
                },
            ] : []}
            filterFn={(option, filterValues) => {
                const search = (filterValues.quickFilter || '').toLowerCase()
                const matchesSearch = !search ||
                    option.label.toLowerCase().includes(search) ||
                    (option.secondaryLabel?.toLowerCase().includes(search) ?? false)
                const matchesTag = !filterValues.tag || (option.data?.tags?.includes(filterValues.tag) ?? false)
                return matchesSearch && matchesTag
            }}
        />
    )
}
